import { useEffect, useState } from "react"
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  DocumentData,
  QueryDocumentSnapshot
} from "firebase/firestore"
import { db } from "../../../../services/firebase"
import "../style/UserList.scss"

type User = {
  id: string
  email: string
  role: string
  createdAt?: string
}

const PAGE_SIZE = 10

const UsersPanel = () => {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null)
  const [pageStack, setPageStack] = useState<QueryDocumentSnapshot<DocumentData>[]>([])

  // Load first page
  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async (
    cursor?: QueryDocumentSnapshot<DocumentData>,
    direction?: "next" | "prev"
  ) => {
    setLoading(true)
    try {
      const usersRef = collection(db, "users")
      let q = query(usersRef, orderBy("createdAt", "desc"), limit(PAGE_SIZE))

      if (direction === "next" && cursor) {
        q = query(usersRef, orderBy("createdAt", "desc"), startAfter(cursor), limit(PAGE_SIZE))
      }

      const snap = await getDocs(q)
      const result: User[] = snap.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          email: data.email,
          role: data.role || "user",
          createdAt: data.createdAt?.toDate?.().toLocaleDateString() || "-"
        }
      })

      setUsers(result)

      if (snap.docs.length > 0) {
        if (direction === "next" && cursor) {
          setPageStack((prev) => [...prev, cursor]) // push cursor for "prev"
        } else if (direction === "prev") {
          setPageStack((prev) => prev.slice(0, -1)) // pop last
        }
        setLastDoc(snap.docs[snap.docs.length - 1])
      }
    } catch (err) {
      console.error("❌ Failed to fetch users:", err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="users-panel">
      <h2>All Users</h2>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={3}>No users found</td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    <td>{u.role}</td>
                    <td>{u.createdAt}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="pagination">
            <button
              disabled={pageStack.length === 0}
              onClick={() => loadUsers(pageStack[pageStack.length - 1], "prev")}
            >
              Previous
            </button>
            <button
              disabled={!lastDoc}
              onClick={() => lastDoc && loadUsers(lastDoc, "next")}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default UsersPanel
